import React, { useState } from 'react';
import { Column, Row } from 'react-foundation';
import * as ArticlesAPI from '../../../../api/ArticlesAPI';
import { StatusPopup } from '../../shared/StatusPopup';
import { convertDateToLongDate } from '../../../../api/GlobalFunctions';

/**
 * Component for the news tab.
 * @param articles the articles that belong to the org
 * @param currentUserCanEdit whether the current user can edit or not (true for yes, false for no)
 */

export const OrganizationNewsTab = ({ articles, currentUserCanEdit }) => {
	/**
	 * ---- Variables/States ----
	 * pageRefresh: refreshes the page when updated
	 * statusPopupObject: Keeps track of the status popup banner component data
	 * showStatusPopupObject: Responsible for showing and hiding the status popup banner component. True for show, false for hide.
	 */
	const [pageRefresh, setPageRefresh] = useState(false);
	const [statusPopupObject, setStatusPopupObject] = useState({});
	const [showStatusPopupObject, setShowStatusPopupObject] = useState(false);

	/**
	 * Handles clicking the create news article button, and redirects the user to the new article page
	 */
	const handleCreateNewsArticle = () => {
		location.assign(`/articles/new`);
	};

	/**
	 * Handles clicking any edit article button, and redirects the user to edit their article
	 * @param id the id of the article to edit
	 */
	const editArticle = (id) => {
		location.assign(`/articles/${id}/edit`);
	};

	/**
	 * Handles clicking any delete article button
	 * @param id the id of the article to delete
	 */
	const deleteArticle = (id) => {
		try {
			ArticlesAPI.deleteArticle(id).then((response) => {
				if (response.status === 204) {
					articles.map((article, index) => {
						if (article.id === id) {
							articles.splice(index, 1);
							setPageRefresh(!pageRefresh);

							let tempShowStatusObject = {
								type: 'success',
								text: 'Successfully deleted article.',
							};
							setStatusPopupObject(tempShowStatusObject);
							setShowStatusPopupObject(true);
						}
					});
				}
			});
		} catch (e) {
			console.error(e);
		}
	};

	if (articles?.length === 0) {
		return (
			<div className="news-tab">
				{currentUserCanEdit ? (
					<div>
						<p className="body-font">
							Do you have an article to share with potential
							sponsors?
						</p>
						<p className="body-font">
							Create an article to showcase more about what your
							organization does!
						</p>
						<div
							className="primary-button"
							onClick={handleCreateNewsArticle}
						>
							<p>Create</p>
						</div>
					</div>
				) : (
					<></>
				)}
			</div>
		);
	}

	return (
		<div className="news-tab">
			{currentUserCanEdit ? (
				<div
					className="primary-button"
					onClick={handleCreateNewsArticle}
				>
					<p>Create Article</p>
				</div>
			) : (
				<></>
			)}

			{showStatusPopupObject ? (
				<StatusPopup
					statusPopupObject={statusPopupObject}
					setShowStatusPopup={setShowStatusPopupObject}
				/>
			) : (
				''
			)}

			<div className="mobile-articles-table">
				{articles?.map((article, index) => {
					return (
						<div className="mobile-articles-container" key={index}>
							<Row className="name-and-title-section">
								<a
									href={`/articles/${article.id}`}
									className="body-font link-font"
								>
									{article.title}
								</a>
							</Row>

							<Row className="info-row">
								<p className="body-font field-key">
									{article.posted_on
										? convertDateToLongDate(
												article.posted_on
										  )
										: ''}
								</p>
							</Row>

							{currentUserCanEdit ? (
								<Row>
									<Column small={12}>
										<div
											className="mobile-edit-div"
											onClick={() =>
												editArticle(article.id)
											}
										>
											<p className="body-font">Edit</p>
											<i className="fas fa-edit cursor-pointer" />
										</div>
										<div
											className="mobile-delete-div"
											onClick={() =>
												deleteArticle(article.id)
											}
										>
											<p className="body-font">Delete</p>
											<i className="fas fa-trash-alt cursor-pointer" />
										</div>
									</Column>
								</Row>
							) : (
								<></>
							)}
						</div>
					);
				})}
			</div>

			<div className="articles-table">
				<table>
					<thead className="articles-header">
						<tr>
							<th className="body-font article-title-header">
								Title
							</th>
							<th className="body-font article-date-header">
								Date
							</th>

							{currentUserCanEdit ? (
								<th className="article-edit-header" />
							) : (
								<></>
							)}
							{currentUserCanEdit ? (
								<th className="article-delete-header" />
							) : (
								<></>
							)}
						</tr>
					</thead>

					<tbody>
						{articles?.map((article, index) => {
							return (
								<tr key={index}>
									<td className="article-title-body">
										<a
											href={`/articles/${article.id}`}
											className="body-font link-font"
										>
											{article.title}
										</a>
									</td>
									<td className="article-date-body">
										<p className="body-font">
											{article.posted_on
												? convertDateToLongDate(
														article.posted_on
												  )
												: ''}
										</p>
									</td>
									{currentUserCanEdit ? (
										<td className="article-edit-header">
											<i
												className="fas fa-edit cursor-pointer"
												onClick={() =>
													editArticle(article.id)
												}
											/>
										</td>
									) : (
										<></>
									)}
									{currentUserCanEdit ? (
										<td className="article-delete-header">
											<i
												className="fas fa-trash-alt cursor-pointer"
												onClick={() =>
													deleteArticle(article.id)
												}
											/>
										</td>
									) : (
										<></>
									)}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};
